import React from 'react';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { ReactQueryConfig, ReactQueryConfigProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query-devtools';
import { Banner, BannerExtension, SkipNav } from '@ver-uds/uswds-react';
import { EnvironmentProvider } from './context/Environment/EnvironmentContext';
import AppRoute from './AppRoute';
import { InternalRoutes } from './constants';
import NrProvider from './components/NrProvider/NrProvider';
import { GlobalProvider } from './context/Global/GlobalContext';
import { TogglesProvider } from './context/Toggles/TogglesContext';

const queryConfig: ReactQueryConfig = {
  queries: {
    refetchOnWindowFocus: false,
    useErrorBoundary: true,
    retry: (failureCount, queryError) => {
      if (queryError instanceof Response) {
        // Don't retry for 4xx errors, because these should always return the same given the same parameters.
        if (queryError.status >= 400 && queryError.status < 500) {
          return false;
        }
      }

      return failureCount < 3;
    },
    onError: (queryError) => {
      if (queryError instanceof Response) {
        if (queryError.status === 401) {
          window.location.href = InternalRoutes.LOGOUT;
        }
      }
    },
  },
  mutations: {
    useErrorBoundary: true,
  },
};

const App = (): JSX.Element => (
  <div className="display-flex flex-column height-viewport">
    <EnvironmentProvider>
      <TogglesProvider>
        <NrProvider>
          <ReactQueryConfigProvider config={queryConfig}>
            <HelmetProvider>
              <Helmet defaultTitle="E-Verify" titleTemplate="%s | E-Verify" />
              <SkipNav.Anchor />
              <Banner extensionType={BannerExtension.GOV} />
              <div className="usa-overlay" />
              <GlobalProvider>
                <AppRoute />
              </GlobalProvider>
            </HelmetProvider>
            <ReactQueryDevtools initialIsOpen={false} />
          </ReactQueryConfigProvider>
        </NrProvider>
      </TogglesProvider>
    </EnvironmentProvider>
  </div>
);

export default App;
